import RouterService from '@ember/routing/router-service';
import Controller, { inject as controller } from '@ember/controller';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import {
  DEFAULT_CAMPAIGN_SETUP_STEPS,
  API_TRIGGERED_BROADCAST_SETUP_STEPS,
  WEBHOOK_CAMPAIGN_SETUP_STEPS,
  CampaignSetupStep,
} from 'cio/utils/campaign-navigation';
import { getOwner } from '@ember/application';

import ApplicationController from 'cio/pods/application/controller';
import CampaignSetupController from 'cio/pods/campaigns/campaign/setup/controller';
import BroadcastSetupController from 'cio/pods/broadcasts/broadcast/setup/controller';
import CampaignModel, { CampaignType } from 'cio/models/campaign';
import FeaturesService from 'ember-feature-flags/services/features';

const TRIGGER_ROUTE = 'campaigns.campaign.setup.trigger.index';

const CAMPAIGN_SETUP_STEPS: Record<CampaignType.APITriggered | CampaignType.Webhook, CampaignSetupStep[] | null> = {
  [CampaignType.APITriggered]: API_TRIGGERED_BROADCAST_SETUP_STEPS,
  [CampaignType.Webhook]: WEBHOOK_CAMPAIGN_SETUP_STEPS,
};

export default class CampaignNavStepController extends Controller {
  @service router!: RouterService;
  @service features!: FeaturesService;

  declare model: CampaignModel;

  @controller application!: ApplicationController;
  @controller('campaigns.campaign.setup') campaignSetup!: CampaignSetupController;
  @controller('broadcasts.broadcast.setup') broadcastSetup!: BroadcastSetupController;

  get currentRouteName() {
    return this.router.currentRouteName;
  }

  get currentController() {
    return getOwner(this).lookup(`controller:${this.currentRouteName}`) as any;
  }

  get campaignType(): CampaignType | null {
    if (this.currentRouteName === TRIGGER_ROUTE) {
      return this.currentController.type;
    }

    if (this.model == null) {
      return null;
    }

    return this.model.type;
  }

  get navSteps(): CampaignSetupStep[] {
    if (isEmpty(this.campaignType)) {
      return [];
    }

    let steps =
      (CAMPAIGN_SETUP_STEPS as Record<string, CampaignSetupStep[]>)[this.campaignType!] ?? DEFAULT_CAMPAIGN_SETUP_STEPS;

    return steps.map((step) => {
      if (!['settings', 'workflow', 'goal', 'review'].includes(step.name)) {
        return step;
      }

      return {
        ...step,
        disabled: this.model.isTypeNone,
      };
    });
  }

  get currentNavStepIndex() {
    let { currentRouteName, navSteps } = this;
    return navSteps.findIndex((step: { path: string }) => currentRouteName.includes(step.path));
  }

  get previousNavStep() {
    let { currentNavStepIndex, navSteps } = this;

    if (currentNavStepIndex === 0) {
      return null;
    }

    return navSteps[currentNavStepIndex - 1];
  }

  get nextNavStep() {
    let { currentNavStepIndex, navSteps } = this;

    if (currentNavStepIndex === navSteps.length - 1) {
      return null;
    }

    return navSteps[currentNavStepIndex + 1];
  }
}
